import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Select, { StylesConfig } from "react-select";
import { Country } from "country-state-city";
import { useTheme } from "../../contexts/ThemeContext";
import {
  useGetTaxesQuery,
  useCreateTaxMutation,
  useEditTaxMutation,
  useDeleteTaxMutation,
} from "../../services";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { adminRoutes } from "../../routers/AdminRouters";
import { ITax, taxTypeOptions, TaxTypeOption } from "../../types";
import { toast } from "../../core/toast";

type BaseOption = { value: string; label: string };
type CountryOption = {
  value: string;
  label: string;
  isoCode: string;
  name: string;
};

const Tax: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  // Component State
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterBy, setFilterBy] = useState<BaseOption | null>(null);
  const [customTaxType, setCustomTaxType] = useState("");
  // API Hooks
  const { data: taxes = [], isLoading } = useGetTaxesQuery({
    search: searchQuery,
    type: filterBy?.value,
  });
  const [createTax, { isLoading: isCreating }] = useCreateTaxMutation();
  const [editTax, { isLoading: isEditing }] = useEditTaxMutation();
  const [deleteTax, { isLoading: isDeleting }] = useDeleteTaxMutation();
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    taxId: string | null;
    taxName: string;
  }>({ open: false, taxId: null, taxName: "" });

  const [formData, setFormData] = useState({
    countryCode: "",
    name: "",
    type: "",
    rate: 0,
    description: "",
  });

  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const countryOptions: CountryOption[] = useMemo(
    () =>
      allCountries.map((c) => ({
        value: c.isoCode,
        label: `${c.name} (${c.isoCode})`,
        isoCode: c.isoCode,
        name: c.name,
      })),
    [allCountries],
  );

  const countryNameByIso = useMemo(() => {
    const map: Record<string, string> = {};
    allCountries.forEach((c) => {
      map[c.isoCode] = c.name;
    });
    return map;
  }, [allCountries]);

  const filterOptions: BaseOption[] = [
    { value: "VAT", label: "VAT" },
    { value: "GST", label: "GST" },
    { value: "Custom", label: "Custom" },
  ];

  // Theme-aware Select Styles
  const selectStyles: StylesConfig<any, false> = useMemo(() => {
    const bg = isDark ? "#111111" : "#ffffff";
    const menuBg = isDark ? "#111111" : "#ffffff";
    const border = isDark ? "#2C2C2C" : "#d1d5db";
    const hoverBorder = "#B6CB24";
    const focusedBg = isDark ? "#1E1E1E" : "#f3f4f6";
    const text = isDark ? "#e5e7eb" : "#111827";
    const subText = isDark ? "#9ca3af" : "#6b7280";

    return {
      control: (base, state) => ({
        ...base,
        backgroundColor: bg,
        borderColor: state.isFocused ? hoverBorder : border,
        boxShadow: "none",
        minHeight: 40,
        borderRadius: "6px",
        ":hover": { borderColor: state.isFocused ? hoverBorder : border },
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: menuBg,
        zIndex: 50,
        border: isDark ? "1px solid #2C2C2C" : "1px solid #d1d5db",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? focusedBg : menuBg,
        color: text,
        cursor: "pointer",
      }),
      singleValue: (base) => ({ ...base, color: text }),
      placeholder: (base) => ({ ...base, color: subText }),
      input: (base) => ({ ...base, color: text }),
      dropdownIndicator: (base) => ({ ...base, color: subText }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: border }),
    };
  }, [isDark]);

  // Handlers
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "rate" ? Number(value) : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      countryCode: "",
      name: "",
      type: "",
      rate: 0,
      description: "",
    });
    setCustomTaxType("");
    setEditingId(null);
  };

  const onSave = async () => {
    if (!formData.countryCode || !formData.type) return;

    const nameToSend =
      formData.type === "Custom" ? customTaxType : formData.type;

    if (formData.type === "Custom" && !customTaxType) {
      toast.error("Please enter a custom tax type name");
      return;
    }

    const payload = {
      country: formData.countryCode,
      name: nameToSend,
      type: formData.type,
      rate: formData.rate,
      description: formData.description,
    };

    try {
      if (editingId) {
        await editTax({ id: editingId, data: payload }).unwrap();
      } else {
        await createTax(payload).unwrap();
      }
      resetForm();
    } catch (err) {
      console.error("Save failed:", err);
    }
  };

  const onEdit = (tax: ITax) => {
    setEditingId(tax._id);

    // Check if the tax name matches one of our predefined options (VAT, GST, etc.)
    const isStandardType = taxTypeOptions.some(
      (opt: TaxTypeOption) => opt.value === tax.name && opt.value !== "Custom",
    );

    if (isStandardType) {
      setFormData({
        countryCode: tax.countryCode,
        name: tax.name,
        type: tax.name,
        rate: tax.rate,
        description: tax.description || "",
      });
      setCustomTaxType("");
    } else {
      // If it's a custom name, select "Custom" option and fill the custom field
      setFormData({
        countryCode: tax.countryCode,
        name: tax.name,
        type: "Custom",
        rate: tax.rate,
        description: tax.description || "",
      });
      setCustomTaxType(tax.name);
    }
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (tax: ITax) => {
    setDeleteConfirm({
      open: true,
      taxId: tax._id,
      taxName: tax.name || tax.type,
    });
  };

  const handleCancelDelete = () => {
    setDeleteConfirm((prev) => ({ ...prev, open: false }));
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.taxId) return;
    try {
      await deleteTax(deleteConfirm.taxId).unwrap();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleteConfirm({ open: false, taxId: null, taxName: "" });
    }
  };

  // Filtered Taxes (Now server-side, so just use taxes from query)
  const filteredTaxes = taxes;

  // Sidebar width stuff
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  // Styles
  const pageBg = isDark ? "bg-[#0B0B0B]" : "bg-gray-50";
  const titleText = isDark ? "text-gray-100" : "text-gray-900";
  const crumbText = isDark ? "text-gray-400" : "text-gray-500";
  const cardClass = isDark
    ? "bg-[#1E1E1E] border border-[#2C2C2C] text-gray-100"
    : "bg-white border border-gray-200 text-gray-900";
  const labelClass = isDark ? "text-[#F3F3F6]" : "text-gray-700";
  const inputClass = isDark
    ? "w-full bg-[#111111] border border-[#2C2C2C] rounded-md px-3 py-2 outline-none focus:border-[#B6CB24] text-gray-100 placeholder-gray-500"
    : "w-full bg-white border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-[#B6CB24] text-gray-900 placeholder-gray-500";

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
        transition: "margin-left 0.3s ease, width 0.3s ease",
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <h1 className={`text-2xl font-bold ${titleText}`}>Tax Overview</h1>
        <div className="flex items-center gap-2 mt-1 text-sm">
          <Link to="/" className={`flex items-center gap-1.5 ${crumbText}`}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 22V12H15V22"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Dashboard
          </Link>
          <span className="text-gray-600">&gt;</span>
          <span className="text-[#B6CB24] font-medium">Tax Details</span>
        </div>
      </div>

      {/* Create Form Section */}
      <div className={`${cardClass} rounded-xl p-6 mb-8`}>
        <h2 className="text-lg font-semibold mb-6">
          {editingId ? "Edit Tax Details" : "Create Tax Details"}
        </h2>
        <div
          className={`grid grid-cols-1 md:grid-cols-2 ${formData.name === "Custom" ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-6 mb-8`}
        >
          <div>
            <label className={`block text-xs font-medium mb-2 ${labelClass}`}>
              Choose Country <span className="text-red-500">*</span>
            </label>
            <Select<CountryOption, false>
              options={countryOptions}
              value={
                formData.countryCode
                  ? countryOptions.find(
                      (opt) => opt.value === formData.countryCode,
                    ) || null
                  : null
              }
              onChange={(opt) =>
                setFormData({
                  ...formData,
                  countryCode: opt?.value || "",
                })
              }
              styles={selectStyles}
              placeholder="Choose from list"
              filterOption={(option, rawInput) => {
                const search = rawInput.toLowerCase();
                const data = option.data as CountryOption;
                return (
                  data.name.toLowerCase().includes(search) ||
                  data.isoCode.toLowerCase().includes(search)
                );
              }}
            />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-2 ${labelClass}`}>
              Choose Tax Type <span className="text-red-500">*</span>
            </label>
            <Select<TaxTypeOption, false>
              options={taxTypeOptions}
              value={
                formData.type
                  ? taxTypeOptions.find(
                      (opt: TaxTypeOption) => opt.value === formData.type,
                    ) || null
                  : null
              }
              onChange={(opt: any) =>
                setFormData({
                  ...formData,
                  type: opt?.value || "",
                  name: opt?.value !== "Custom" ? opt?.value : "",
                })
              }
              styles={selectStyles}
              placeholder="Choose from list"
            />
          </div>

          {formData.type === "Custom" && (
            <div>
              <label className={`block text-xs font-medium mb-2 ${labelClass}`}>
                Custom Tax Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={customTaxType}
                onChange={(e) => setCustomTaxType(e.target.value)}
                className={inputClass}
                placeholder="Enter tax type name"
              />
            </div>
          )}

          <div>
            <label className={`block text-xs font-medium mb-2 ${labelClass}`}>
              Rate <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                name="rate"
                value={formData.rate}
                onChange={handleInputChange}
                className={inputClass}
                placeholder="0"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                %
              </span>
            </div>
          </div>
          <div>
            <label className={`block text-xs font-medium mb-2 ${labelClass}`}>
              Enter Description
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className={inputClass}
              placeholder="Enter here"
            />
          </div>
        </div>
        <div className="flex gap-4">
          <button
            onClick={onSave}
            disabled={isCreating || isEditing}
            className="px-10 py-2.5 rounded-lg text-black font-semibold transition-all hover:opacity-90 disabled:opacity-50"
            style={{
              background: "linear-gradient(90deg, #B6CB24 0%, #8DA11B 100%)",
            }}
          >
            {isCreating || isEditing ? "Saving..." : "Save"}
          </button>
          <button
            onClick={resetForm}
            className={`px-10 py-2.5 rounded-lg font-semibold border ${
              isDark
                ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                : "border-gray-300 text-gray-700 hover:bg-gray-100"
            }`}
          >
            Back
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className={`${cardClass} rounded-xl overflow-hidden`}>
        <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <h2 className="text-lg font-semibold">All Tax Details</h2>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 21L15.5 15.5M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search in table"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${inputClass} pl-10 bg-[#111111]`}
              />
            </div>
            <div className="w-40">
              <Select<BaseOption, false>
                options={filterOptions}
                value={filterBy}
                onChange={(opt) => setFilterBy(opt as BaseOption | null)}
                styles={selectStyles}
                placeholder="Tax Type"
                isClearable
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr
                className={`${isDark ? "bg-[#1E1E1E]" : "bg-gray-100"} text-gray-500 text-xs uppercase tracking-wider border-b border-gray-800`}
              >
                <th className="px-6 py-4 font-semibold">Country</th>
                <th className="px-6 py-4 font-semibold">Tax Type</th>
                <th className="px-6 py-4 font-semibold">Rate %</th>
                <th className="px-6 py-4 font-semibold">Description</th>
                <th className="px-6 py-4 font-semibold text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    Loading taxes...
                  </td>
                </tr>
              ) : filteredTaxes.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No tax details found.
                  </td>
                </tr>
              ) : (
                filteredTaxes.map((tax) => (
                  <tr
                    key={tax._id}
                    className={`${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"} transition-colors`}
                  >
                    <td className="px-6 py-4 text-sm">
                      {countryNameByIso[tax.country] || tax.name}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {tax.name || tax.type}
                    </td>
                    <td className="px-6 py-4 text-sm">{tax.rate}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {tax.description || "-"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center items-center gap-4">
                        <button
                          onClick={() => onEdit(tax)}
                          className="p-1 hover:opacity-80 transition-opacity"
                          title="Edit"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z"
                              stroke="#B6CB24"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteClick(tax)}
                          className="p-1 hover:opacity-80 transition-opacity"
                          title="Delete"
                          disabled={isDeleting}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M3 6H5H21M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19ZM8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M10 11V17M14 11V17"
                              stroke="#D11A2A"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {deleteConfirm.open && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
            theme === "dark" ? "bg-black/60" : "bg-black/40"
          }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${
              theme === "dark"
                ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
                : "bg-white text-gray-900 border-gray-200"
            }`}
          >
            <button
              onClick={handleCancelDelete}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${
                theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
              }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>

            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${
                  theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M19.5 5.5L19.0982 12.0062M4.5 5.5L5.10461 15.5248C5.25945 18.0922 5.33688 19.3759 5.97868 20.299C6.296 20.7554 6.7048 21.1407 7.17905 21.4302C7.85035 21.84 8.68108 21.9631 10 22"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M20 15L13 21.9995M20 22L13 15.0005"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Delete Tax</h3>
              <p
                className={`$${
                  theme === "dark" ? "text-gray-300" : "text-gray-600"
                } mb-6`}
              >
                Are you sure you want to delete "{deleteConfirm.taxName}"?
              </p>

              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid #D92C2C",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Confirm and delete"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-black rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  May be later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tax;
